// studio/SideViews.jsx — Vues secondaires du studio : Vue d'ensemble, Projets, Agents IA, Déploiements, Bibliothèque, Environnements, Monitoring, Paramètres.
import { ST, PNL, chip, STATUS_META } from "@/internal/studio/theme";
import { AGENTS, getCredits } from "@/internal/studio/simEngine";
import { Bot, Rocket, Boxes, Server, Activity, Settings, FolderKanban, Gauge } from "lucide-react";

const Card = ({ children, style }) => <div style={{ ...PNL, padding: 16, ...style }}>{children}</div>;
const H = ({ icon: I, children }) => <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 900, color: ST.text, marginBottom: 12 }}>{I && <I size={16} color={ST.green} />}{children}</div>;
const Stat = ({ label, value, c = ST.green }) => (
  <Card><div style={{ fontSize: 24, fontWeight: 900, color: c }}>{value}</div><div style={{ fontSize: 11, color: ST.muted, fontWeight: 700, marginTop: 4 }}>{label}</div></Card>
);
const BuildRow = ({ b, onOpen }) => {
  const m = STATUS_META[b.status] || STATUS_META.plan;
  return (
    <button onClick={() => onOpen(b.build_id)} data-testid={`project-row-${b.build_id}`} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left", background: ST.card, border: `1px solid ${ST.border}`, borderRadius: 12, padding: "11px 14px", marginBottom: 8, cursor: "pointer" }}>
      <FolderKanban size={15} color={ST.blue} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 800, color: ST.text }}>{b.plan?.title}</div>
        <div style={{ fontSize: 10.5, color: ST.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.intent}</div>
      </div>
      <span style={chip(m.c)}>{m.l}</span>
    </button>
  );
};

export function OverviewView({ builds, onOpen }) {
  const dep = builds.filter((b) => b.status === "deployed").length;
  const val = builds.filter((b) => b.status === "validation").length;
  return (
    <div style={{ overflowY: "auto", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10 }}>
        <Stat label="Builds Creator Studio" value={builds.length} c={ST.blue} />
        <Stat label="Déployés" value={dep} />
        <Stat label="En validation Founder" value={val} c={ST.gold} />
        <Stat label="Crédits AYA restants" value={getCredits().toLocaleString("fr-FR")} c={ST.purple} />
      </div>
      <Card><H icon={Gauge}>Derniers builds</H>{builds.slice(0, 6).map((b) => <BuildRow key={b.build_id} b={b} onOpen={onOpen} />)}{builds.length === 0 && <div style={{ fontSize: 12, color: ST.muted }}>Aucun build pour l'instant.</div>}</Card>
    </div>
  );
}

export function ProjectsView({ builds, onOpen }) {
  return <div style={{ overflowY: "auto" }}><Card><H icon={FolderKanban}>Projets · {builds.length} build(s)</H>{builds.map((b) => <BuildRow key={b.build_id} b={b} onOpen={onOpen} />)}{builds.length === 0 && <div style={{ fontSize: 12, color: ST.muted }}>Aucun projet. Lancez une intention dans le Creator Studio.</div>}</Card></div>;
}

export function AgentsView() {
  const DESC = { "Architect AI": "Cartographie l'existant AYA et décide quoi réutiliser avant de reconstruire.", "Developer AI": "Génère le code React / Node en respectant les conventions AYA (français, data-testid).", "UI AI": "Garantit la cohérence avec le design system AYA Internal (sombre, vert premium).", "QA AI": "Écrit et exécute les tests, bloque tout build qui casse l'existant.", "Media AI": "Produit les assets visuels et vidéos promotionnelles (optionnel)." };
  return (
    <div style={{ overflowY: "auto", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 10 }}>
      {AGENTS.map((a) => (
        <Card key={a.name}><H icon={Bot}>{a.name}</H><div style={{ fontSize: 12, color: ST.muted, lineHeight: 1.6 }}>{DESC[a.name]}</div>
          <div style={{ marginTop: 10 }}><span style={chip(a.optional ? ST.gold : ST.green)}>{a.optional ? "optionnel" : "actif"}</span></div></Card>
      ))}
    </div>
  );
}

export function DeploymentsView({ builds, onOpen }) {
  const rows = builds.filter((b) => ["deployed", "validation", "denied"].includes(b.status));
  return <div style={{ overflowY: "auto" }}><Card><H icon={Rocket}>Déploiements</H>{rows.map((b) => <BuildRow key={b.build_id} b={b} onOpen={onOpen} />)}{rows.length === 0 && <div style={{ fontSize: 12, color: ST.muted }}>Aucun déploiement. Les builds validés par le Founder apparaîtront ici.</div>}</Card></div>;
}

export function LibraryView() {
  const ITEMS = [["Boutons", "12 variantes AYA"], ["Cartes", "8 modèles premium"], ["Formulaires", "6 blocs validés"], ["Navigation", "4 patterns internes"], ["Badges & Chips", "10 statuts"], ["Graphiques", "5 widgets dashboard"]];
  return (
    <div style={{ overflowY: "auto", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 10 }}>
      {ITEMS.map(([t, d]) => <Card key={t}><H icon={Boxes}>{t}</H><div style={{ fontSize: 11.5, color: ST.muted }}>{d}</div><div style={{ marginTop: 10, height: 36, borderRadius: 9, background: "linear-gradient(90deg, rgba(46,226,123,0.12), rgba(56,189,248,0.08))", border: `1px solid ${ST.border}` }} /></Card>)}
    </div>
  );
}

export function EnvironmentsView() {
  const ENVS = [["Développement", "dev.aya.internal", ST.green, "Actif"], ["Staging", "staging.aya.internal", ST.gold, "Prêt"], ["Production", "app.aya.ci", ST.red, "Protégé JSONFIRST"]];
  return (
    <div style={{ overflowY: "auto", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 10 }}>
      {ENVS.map(([n, url, c, s]) => <Card key={n}><H icon={Server}>{n}</H><div style={{ fontSize: 11.5, fontFamily: ST.mono, color: ST.muted }}>{url}</div><div style={{ marginTop: 10 }}><span style={chip(c)}>{s}</span></div></Card>)}
    </div>
  );
}

export function MonitoringView() {
  const M = [["Uptime plateforme", "99.98 %", ST.green], ["Latence API p95", "184 ms", ST.blue], ["Erreurs 24h", "0", ST.green], ["Builds cette semaine", "—", ST.muted]];
  return (
    <div style={{ overflowY: "auto", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 10 }}>
      {M.map(([l, v, c]) => <Stat key={l} label={l} value={v} c={c} />)}
      <Card style={{ gridColumn: "1 / -1" }}><H icon={Activity}>Activité (SIMULÉ)</H>
        <div style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 70 }}>
          {[34, 52, 41, 66, 58, 74, 49, 81, 63, 70, 55, 88, 61, 77].map((h, i) => <div key={i} style={{ flex: 1, height: `${h}%`, borderRadius: 4, background: `rgba(46,226,123,${0.25 + h / 200})` }} />)}
        </div></Card>
    </div>
  );
}

export function SettingsView({ dataMode, onToggleMode }) {
  const prod = dataMode === "production";
  const ROWS = [["Édition manuelle du code", "Désactivée par politique AYA : tout changement passe par une intention.", false, null], ["Validation JSONFIRST", "Chaque déploiement exige une approbation Founder.", true, null]];
  const Toggle = ({ on, onClick }) => (
    <div onClick={onClick} style={{ width: 36, height: 20, borderRadius: 999, background: on ? "rgba(46,226,123,0.3)" : "rgba(139,163,148,0.2)", position: "relative", cursor: onClick ? "pointer" : "default" }}>
      <div style={{ position: "absolute", top: 2, left: on ? 18 : 2, width: 16, height: 16, borderRadius: "50%", background: on ? ST.green : ST.muted, transition: "left 0.2s" }} />
    </div>
  );
  return (
    <div style={{ overflowY: "auto" }}>
      <Card><H icon={Settings}>Paramètres du studio</H>
        <div data-testid="settings-datamode-row" style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${ST.border2}` }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12.5, fontWeight: 800, color: ST.text }}>Mode données : {prod ? "PRODUCTION (données réelles)" : "SIMULATION (mocks actifs)"}</div>
            <div style={{ fontSize: 11, color: ST.muted }}>{prod ? "Les simulations sont désactivées dans tous les studios. Basculer désactive ce mode." : "Passez en production pour supprimer les mocks — gouverné par JSONFIRST. Commande CTO : « Passe en production, supprime les mocks »."}</div>
          </div>
          <Toggle on={prod} onClick={onToggleMode} />
        </div>
        {ROWS.map(([t, d, on]) => (
          <div key={t} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: `1px solid ${ST.border2}` }}>
            <div style={{ flex: 1 }}><div style={{ fontSize: 12.5, fontWeight: 800, color: ST.text }}>{t}</div><div style={{ fontSize: 11, color: ST.muted }}>{d}</div></div>
            <Toggle on={on} />
          </div>
        ))}
      </Card>
    </div>
  );
}
